import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DataDisclaimer from "../../components/DataDisclaimer/DataDisclaimer";
import Layout from "../../components/Layout/Layout";
import { fetchAnomalies, fetchFeatures, fetchTypes } from "../../lib/api";
import { exportUrl } from "../../lib/csv";
import { formatCoords, formatEventDate, TYPE_LABELS } from "../../lib/format";
import type { Feature, LunarEvent, PhenomenonType, PhenomenonTypeId } from "../../types";

const PAGE_SIZES = [10, 25, 50, 100, 250] as const;
const DEFAULT_PAGE_SIZE = 25;
const DESC_MAX = 250;

function parsePageSize(raw: string | null): number {
  const n = Number(raw);
  return PAGE_SIZES.includes(n as (typeof PAGE_SIZES)[number])
    ? n
    : DEFAULT_PAGE_SIZE;
}

function truncate(text: string, max: number): string {
  const compact = text.replace(/\s+/g, " ").trim();
  if (compact.length <= max) return compact;
  const cut = compact.slice(0, max);
  const bounded = cut.replace(/\s+\S*$/, "");
  return `${bounded || cut}…`;
}

const Catalog = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<LunarEvent[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [types, setTypes] = useState<PhenomenonType[]>([]);
  const [error, setError] = useState<string | null>(null);

  const q = searchParams.get("q") ?? "";
  const typeFilter = (searchParams.get("type") ?? "") as PhenomenonTypeId | "";
  const featureId = searchParams.get("feature") ?? "";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const pageSize = parsePageSize(searchParams.get("perPage"));

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchAnomalies({ pageSize: 5000 }),
      fetchFeatures(),
      fetchTypes(),
    ])
      .then(([anomalyPage, featureList, typeList]) => {
        if (cancelled) return;
        setEvents(anomalyPage.items);
        setFeatures(featureList);
        setTypes(typeList);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return events.filter((event) => {
      if (typeFilter && event.phenomenonTypeId !== typeFilter) return false;
      if (featureId && event.featureId !== featureId) return false;
      if (!query) return true;
      const hay = [
        event.description,
        event.locationText,
        event.featureName,
        event.observer,
        event.reference,
        event.dateText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });
  }, [events, q, typeFilter, featureId]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageItems = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in patch)) {
      next.delete("page");
    }
    setSearchParams(next);
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-12 w-full">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-alt-blk mb-2">Catalog</h1>
            <p className="text-alt-blk/70">
              {filtered.length.toLocaleString()} reports from Middlehurst 1968
              and Cameron 1978/2006.
            </p>
            <div className="mt-2">
              <DataDisclaimer compact />
            </div>
          </div>
          {filtered.length > 0 ? (
            <a
              href={exportUrl({
                q: q.trim() || undefined,
                type: typeFilter || undefined,
                featureId: featureId || undefined,
              })}
              className="inline-block bg-mars hover:bg-sunset text-white px-6 py-3 rounded-2xl font-semibold text-center whitespace-nowrap"
            >
              Export CSV
            </a>
          ) : (
            <span className="inline-block bg-mars/40 text-white px-6 py-3 rounded-2xl font-semibold text-center whitespace-nowrap cursor-not-allowed">
              Export CSV
            </span>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <input
            type="search"
            value={q}
            onChange={(e) => update({ q: e.target.value })}
            placeholder="Search the archive…"
            className="rounded-xl border border-alt-blk/15 bg-white px-4 py-3 md:col-span-1"
          />
          <select
            value={typeFilter}
            onChange={(e) => update({ type: e.target.value })}
            className="rounded-xl border border-alt-blk/15 bg-white px-4 py-3"
          >
            <option value="">All types</option>
            {types.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
          <select
            value={featureId}
            onChange={(e) => update({ feature: e.target.value })}
            className="rounded-xl border border-alt-blk/15 bg-white px-4 py-3"
          >
            <option value="">All features</option>
            {features.map((feature) => (
              <option key={feature.id} value={feature.id}>
                {feature.name} ({feature.eventCount})
              </option>
            ))}
          </select>
        </div>

        {error && <p className="text-mars mb-6">{error}</p>}

        <div className="md:hidden space-y-4">
          {pageItems.map((event) => (
            <Link
              key={event.id}
              to={`/events/${event.id}`}
              className="block rounded-2xl border border-alt-blk/10 bg-white p-4 hover:bg-alt-wht/60"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="text-turquoise font-semibold">
                  {formatEventDate(event)}
                </span>
                <span className="text-xs text-sun font-semibold uppercase tracking-wide shrink-0">
                  {TYPE_LABELS[event.phenomenonTypeId]}
                </span>
              </div>
              <h2 className="text-lg font-bold text-alt-blk mt-2">
                {event.featureName ?? event.locationText}
              </h2>
              {event.latitude != null && event.longitude != null ? (
                <p className="text-xs text-alt-blk/50 mt-0.5">
                  {formatCoords(event.latitude, event.longitude)}
                </p>
              ) : (event.featureName ?? event.locationText) !== "Unlocated" ? (
                <p className="text-xs text-alt-blk/50 mt-0.5">Unlocated</p>
              ) : null}
              <p className="mt-3 text-sm leading-relaxed overflow-hidden">
                {truncate(event.description, DESC_MAX)}
              </p>
              {event.observer && (
                <p className="mt-3 text-xs text-alt-blk/55">
                  Observer: {event.observer}
                </p>
              )}
            </Link>
          ))}
        </div>

        <div className="hidden md:block overflow-x-auto rounded-2xl border border-alt-blk/10 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-alt-blk text-alt-wht text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Feature</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 font-semibold">Observer</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((event) => (
                <tr
                  key={event.id}
                  className="border-t border-alt-blk/10 hover:bg-alt-wht/60 cursor-pointer"
                  onClick={() => navigate(`/events/${event.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/events/${event.id}`);
                    }
                  }}
                  tabIndex={0}
                  role="link"
                >
                  <td className="px-4 py-3 whitespace-nowrap align-top text-turquoise font-medium">
                    {formatEventDate(event)}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div>{event.featureName ?? event.locationText}</div>
                    <div className="text-xs text-alt-blk/50">
                      {formatCoords(event.latitude, event.longitude)}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    {TYPE_LABELS[event.phenomenonTypeId]}
                  </td>
                  <td className="px-4 py-3 align-top max-w-md overflow-hidden">
                    <p className="overflow-hidden text-ellipsis">
                      {truncate(event.description, DESC_MAX)}
                    </p>
                  </td>
                  <td className="px-4 py-3 align-top">{event.observer ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 text-sm">
          <button
            disabled={currentPage <= 1}
            onClick={() => update({ page: String(currentPage - 1) })}
            className="px-4 py-2 rounded-xl bg-alt-blk text-white disabled:opacity-40"
          >
            Previous
          </button>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span>
              Page {currentPage} of {pageCount}
            </span>
            <label className="flex items-center gap-2 text-alt-blk/70">
              Records per page
              <select
                value={pageSize}
                onChange={(e) =>
                  update({
                    perPage:
                      e.target.value === String(DEFAULT_PAGE_SIZE)
                        ? ""
                        : e.target.value,
                    page: "1",
                  })
                }
                className="rounded-xl border border-alt-blk/15 bg-white px-3 py-2 text-alt-blk"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            disabled={currentPage >= pageCount}
            onClick={() => update({ page: String(currentPage + 1) })}
            className="px-4 py-2 rounded-xl bg-alt-blk text-white disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </section>
    </Layout>
  );
};

export default Catalog;
